import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { vpsData, storagePricing, LINUX_DISTROS } from "../../assets/data/vpsData";
import { customLimits } from "../../assets/data/customLimits";
import axios from "axios";
import { toast } from "react-toastify";
import StepIndicator from './StepIndicator';
import PlanStep from './PlanStep';
import CustomSpecStep from './CustomSpecStep';
import AddonsStep from './AddonsStep';
import DedicatedIPsInput from './DedicatedIPsInput';
import DetailsStep from './DetailsStep';
import ReviewStep from './ReviewStep';
import useQuote from "../services/useQuote";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://192.20.192.87:7501/api";

const WINDOWS_VERSIONS = ["Windows Server 2019", "Windows Server 2022"];

const SubscriptionModal = ({ plan, onClose }) => {
  const isCustom = !!plan?.isCustom;
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(plan?.plan || (isCustom ? "Custom" : ""));
  const [selectedOS, setSelectedOS] = useState("Linux");
  const [selectedVariant, setSelectedVariant] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [billingCycle, setBillingCycle] = useState("Monthly");
  const [customerName, setCustomerName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dedicatedIPs, setDedicatedIPs] = useState(0);
  const [extraStorage, setExtraStorage] = useState({ nvme: 0, hdd: 0 });
  // Custom-spec state (manual entry)
  const [cpuCores, setCpuCores] = useState(customLimits.cpu.default);
  const [ramGb, setRamGb] = useState(customLimits.ramGb.default);
  const [customNvme, setCustomNvme] = useState(customLimits.nvmeGb.default);
  const [customHdd, setCustomHdd] = useState(customLimits.hddGb.default);

  const selectedData = useMemo(() => {
    if (isCustom) return { plan: "Custom", cpu: `${cpuCores} vCPU`, ram: `${ramGb} GB`, storage: `${customNvme} GB NVMe / ${customHdd} GB SSD` };
    if (!selectedPlan) return null;
    return vpsData.find((p) => p.plan === selectedPlan);
  }, [selectedPlan, isCustom, cpuCores, ramGb, customNvme, customHdd]);

  const isStorageOnly = selectedData?.isStorageOnly || false;
  const totalSteps = isStorageOnly ? 3 : 4;

  const modalType = useMemo(() => (plan?.isStorageOnly ? "storage" : "vps"), [plan]);

  const { variants, variantLabel, parsedBasePrice } = useMemo(() => {
    if (isCustom) {
      const list = selectedOS === "Linux" ? LINUX_DISTROS : WINDOWS_VERSIONS;
      return {
        variants: list,
        variantLabel: selectedOS === "Linux" ? "Linux Distribution" : "Windows Version",
        parsedBasePrice: 0,
      };
    }
    if (isStorageOnly) {
      const storageOpts = selectedData?.osOptions?.storage || {};
      const price = parseFloat(String(storageOpts.price || "").replace(/GHS\s?/, "").replace(/,/g, "")) || 0;
      return { variants: [], variantLabel: "", parsedBasePrice: price };
    }
    const osOpts = selectedData?.osOptions?.[selectedOS.toLowerCase()] || {};
    const basePriceRaw = osOpts?.price || "GHS 0";
    const price = parseFloat(String(basePriceRaw).replace(/GHS\s?/, "").replace(/,/g, "")) || 0;
    const variantList = selectedOS === "Linux" ? osOpts?.distros || [] : osOpts?.versions || [];
    const label = selectedOS === "Linux" ? "Linux Distribution" : "Windows Version";
    return { variants: variantList, variantLabel: label, parsedBasePrice: price };
  }, [selectedOS, isStorageOnly, isCustom, selectedData]);

  useEffect(() => {
    if (variants.length > 0) {
      setSelectedVariant(variants[0]);
    } else {
      setSelectedVariant("");
    }
  }, [variants]);

  const billingMonths = billingCycle === "Quarterly" ? 3 : billingCycle === "Annually" ? 12 : 1;

  // Server-authoritative quote (debounced) with client fallback
  const quoteInput = useMemo(() => {
    if (isCustom) {
      return {
        plan: "Custom", isCustom: true,
        cpuCores, ramGb, nvmeGb: customNvme, hddGb: customHdd,
        os: selectedOS, dedicatedIPs, quantity, billingCycle, basePrice: 0,
      };
    }
    return {
      plan: selectedPlan, isCustom: false,
      cpuCores: 0, ramGb: 0, nvmeGb: extraStorage.nvme || 0, hddGb: extraStorage.hdd || 0,
      os: selectedOS, dedicatedIPs: isStorageOnly ? 0 : dedicatedIPs,
      quantity, billingCycle, basePrice: parsedBasePrice,
    };
  }, [isCustom, selectedPlan, cpuCores, ramGb, customNvme, customHdd, selectedOS, dedicatedIPs, quantity, billingCycle, extraStorage, parsedBasePrice, isStorageOnly]);

  const { quote, rates } = useQuote(quoteInput);
  const IP_PRICE = rates?.IP_EACH ?? 15.00;
  const NVME_PRICE = rates?.NVME_50GB ?? storagePricing.NVMe.pricePer50GB;
  const HDD_PRICE = rates?.HDD_50GB ?? storagePricing.HDD.pricePer50GB;

  // Legacy client math as fallback while quote loads
  const legacyNvmeUnits = Math.ceil((isCustom ? customNvme : (extraStorage.nvme || 0)) / 50) || 0;
  const legacyHddUnits = Math.ceil((isCustom ? customHdd : (extraStorage.hdd || 0)) / 50) || 0;
  const legacyStorage = legacyNvmeUnits * NVME_PRICE + legacyHddUnits * HDD_PRICE;
  const legacyIp = isStorageOnly ? 0 : dedicatedIPs * IP_PRICE;
  const legacyBase = isCustom ? (cpuCores * (rates?.VCPU ?? 40) + ramGb * (rates?.RAM_GB ?? 30) + legacyStorage) : (parsedBasePrice + (isStorageOnly ? 0 : legacyStorage));
  const legacyMonthly = ((legacyBase + legacyIp) * quantity);
  const legacySubtotal = legacyMonthly * billingMonths;
  const legacyNhil = legacySubtotal * 0.025;
  const legacyGetfund = legacySubtotal * 0.025;
  const legacyVat = (legacySubtotal + legacyNhil + legacyGetfund) * 0.15;
  const legacyTotal = legacySubtotal + legacyNhil + legacyGetfund + legacyVat;

  const pricePerMonth = quote ? quote.pricePerMonth : legacyMonthly.toFixed(2);
  const subtotal = quote ? quote.subtotal : legacySubtotal.toFixed(2);
  const nhil = quote ? quote.nhil : legacyNhil.toFixed(2);
  const getfund = quote ? quote.getfund : legacyGetfund.toFixed(2);
  const vat = quote ? quote.vat : legacyVat.toFixed(2);
  const totalPrice = quote ? quote.total : legacyTotal.toFixed(2);

  // Display helpers for legacy props
  const storagePriceTotal = isCustom ? legacyStorage * quantity : (((extraStorage.nvme || 0) / 50) * NVME_PRICE + ((extraStorage.hdd || 0) / 50) * HDD_PRICE) * quantity;
  const ipPriceTotal = isStorageOnly ? 0 : dedicatedIPs * IP_PRICE;

  // Dates calculation
  const startDateObj = new Date();
  startDateObj.setDate(startDateObj.getDate() + 2);
  const startDate = startDateObj.toLocaleDateString("en-GB");

  const expirationDateObj = new Date(startDateObj);
  expirationDateObj.setMonth(expirationDateObj.getMonth() + billingMonths);
  const formattedExpirationDate = expirationDateObj.toLocaleDateString("en-GB");

  // Healthcheck
  useEffect(() => {
    const testServerConnection = async () => {
      try {
        await axios.get(`${API_BASE_URL}/health`);
      } catch {
        toast.warning("Backend server is not connected.");
      }
    };
    testServerConnection();
  }, []);

  const validateAllFields = () => {
    if (!customerName.trim()) return false;
    if (!email.trim() || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) return false;
    if (!phoneNumber || phoneNumber.length < 3) return false;
    if (!isStorageOnly && variants.length > 0 && !selectedVariant) return false;
    if (isCustom && (cpuCores < 1 || ramGb < 2)) return false;
    if (!agreeTerms) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting || isLoading) return;

    if (!validateAllFields()) {
      toast.error("Please ensure all validation details are filled correctly.");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    const effectiveNvme = isCustom ? customNvme : (isStorageOnly ? 0 : (extraStorage.nvme || 0));
    const effectiveHdd = isCustom ? customHdd : (isStorageOnly ? 0 : (extraStorage.hdd || 0));

    const subscriptionData = {
      name: customerName,
      email: email,
      phone: phoneNumber,
      plan: isCustom ? "Custom" : selectedPlan,
      os: isStorageOnly ? "Storage Only" : `${selectedOS} (${selectedVariant})`,
      quantity: quantity,
      billingCycle: billingCycle,
      pricePerMonth: parseFloat(pricePerMonth),
      totalPrice: parseFloat(totalPrice),
      subtotal: parseFloat(subtotal),
      nhil: parseFloat(nhil),
      getfund: parseFloat(getfund),
      vat: parseFloat(vat),
      isCustom: isCustom,
      cpuCores: isCustom ? cpuCores : 0,
      ramGb: isCustom ? ramGb : 0,
      basePrice: isCustom ? 0 : parsedBasePrice,
      dedicatedIPs: isStorageOnly ? 0 : dedicatedIPs,
      ipPriceTotal: isStorageOnly ? 0 : parseFloat(ipPriceTotal.toFixed ? ipPriceTotal.toFixed(2) : ipPriceTotal),
      extraStorage: { nvme: effectiveNvme, hdd: effectiveHdd },
      nvmeGb: effectiveNvme,
      hddGb: effectiveHdd,
      storagePriceTotal: isStorageOnly ? 0 : parseFloat(storagePriceTotal.toFixed ? storagePriceTotal.toFixed(2) : storagePriceTotal),
      autoRenewal: autoRenewal,
      start: startDate,
      expiration: formattedExpirationDate,
      callbackUrl: process.env.REACT_APP_CALLBACK_URL || "https://acs.andurar.com/payment/callback",
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/payment/initialize`,
        subscriptionData,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.authorizationUrl) {
        sessionStorage.setItem("paystack_reference", response.data.reference);
        window.location.href = response.data.authorizationUrl;
      } else {
        toast.error("Could not get payment URL. Please try again.");
        setIsSubmitting(false);
        setLoading(false);
      }
    } catch (error) {
      console.error("Payment init error:", error);
      toast.error("Failed to initialize payment. Please try again.");
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (isCustom) {
        if (cpuCores < 1) return toast.error("Enter at least 1 vCPU");
        if (ramGb < 2) return toast.error("RAM starts from 2 GB");
        if (!selectedVariant && variants.length > 0) return toast.error(`Please select a ${variantLabel}`);
      } else {
        if (!selectedPlan) return toast.error("Please select a plan");
        if (!isStorageOnly && variants.length > 0 && !selectedVariant) {
          return toast.error(`Please select a ${variantLabel}`);
        }
      }
      if (isStorageOnly) {
        setCurrentStep(3);
        return;
      }
    }

    if (currentStep === 3) {
      if (!customerName.trim()) return toast.error("Please enter your name");
      if (!email.trim() || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
        return toast.error("Please enter a valid email address");
      }
      if (!phoneNumber || phoneNumber.length < 3) return toast.error("Please enter your phone number");
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      if (currentStep === 3 && isStorageOnly) {
        setCurrentStep(1);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const canSubmit = isCustom ? (cpuCores >= 1 && ramGb >= 2) : !!selectedData;

  // Keep the page behind the dialog still while it's open
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Portal to <body>: the pricing section's AOS animation leaves a CSS transform on
  // its wrapper, which would otherwise anchor this fixed overlay to the section
  // instead of the screen (dialog appeared half off-screen).
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div
        className={`bg-[#001F3F] border-2 border-[#022A52] rounded-3xl p-6 w-full relative text-white shadow-2xl ${
          isCustom ? "max-w-xl" : "max-w-lg"
        } max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-300 hover:text-red-500 text-2xl font-bold transition"
        >
          ×
        </button>

        <h2 className="text-xl font-bold mb-4 text-center text-white tracking-wide">
          {isStorageOnly ? "Subscribe to Cloud Storage" : isCustom ? "Build Your Own VPS" : "Subscribe to VPS Plan"}
        </h2>
        <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

        <form onSubmit={handleSubmit}>
          {currentStep === 1 && (
            isCustom ? (
              <CustomSpecStep
                cpuCores={cpuCores} setCpuCores={setCpuCores}
                ramGb={ramGb} setRamGb={setRamGb}
                customNvme={customNvme} setCustomNvme={setCustomNvme}
                customHdd={customHdd} setCustomHdd={setCustomHdd}
                selectedOS={selectedOS} setSelectedOS={setSelectedOS}
                variants={variants} selectedVariant={selectedVariant} setSelectedVariant={setSelectedVariant}
                variantLabel={variantLabel} billingCycle={billingCycle} setBillingCycle={setBillingCycle}
                quantity={quantity} setQuantity={setQuantity}
              />
            ) : (
              <PlanStep
                selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan}
                selectedOS={selectedOS} setSelectedOS={setSelectedOS}
                variants={variants} selectedVariant={selectedVariant} setSelectedVariant={setSelectedVariant}
                variantLabel={variantLabel} billingCycle={billingCycle} setBillingCycle={setBillingCycle}
                quantity={quantity} setQuantity={setQuantity}
                isStorageOnly={isStorageOnly}
                type={modalType}
              />
            )
          )}

          {currentStep === 2 && !isStorageOnly && !isCustom && (
            <AddonsStep
              dedicatedIPs={dedicatedIPs} setDedicatedIPs={setDedicatedIPs}
              ipPriceTotal={ipPriceTotal} IP_PRICE_PER_UNIT={IP_PRICE}
              extraStorage={extraStorage} setExtraStorage={setExtraStorage}
              storagePriceTotal={storagePriceTotal} STORAGE_PRICE_PER_UNIT={NVME_PRICE}
            />
          )}

          {currentStep === 2 && !isStorageOnly && isCustom && (
            <div className="space-y-4 animation-fade-in">
              <div className="bg-[#022A52] bg-opacity-40 p-3 rounded-xl border border-[#033A6A]">
                <p className="text-xs text-gray-300 leading-relaxed">
                  💡 Storage already included in your custom specs above. Add dedicated IPs here if needed.
                </p>
              </div>
              <DedicatedIPsInput
                dedicatedIPs={dedicatedIPs}
                setDedicatedIPs={setDedicatedIPs}
                ipPriceTotal={ipPriceTotal}
                IP_PRICE_PER_UNIT={IP_PRICE}
              />
            </div>
          )}

          {currentStep === 2 && isStorageOnly && (
            <div className="bg-blue-900/30 p-6 rounded-xl border border-blue-500/30 text-center">
              <p className="text-blue-300 text-lg">📦 Cloud Storage Plan</p>
              <p className="text-gray-300 text-sm mt-2">
                {selectedData?.osOptions?.storage?.description || "Pure cloud storage without compute resources"}
              </p>
              <p className="text-white text-xl font-bold mt-4">GHS {pricePerMonth}/month</p>
            </div>
          )}

          {/* Live levy preview — compact single strip, no scroll push */}
          {(currentStep === 1 || currentStep === 2) && !isStorageOnly && (
            <div className="mt-3 text-xs bg-[#022A52] rounded-xl px-3 py-2 border border-[#033A6A] flex items-center justify-between gap-2 flex-wrap">
              <span className="text-gray-300">SUB-TOTAL GHS {subtotal}</span>
              <span className="text-gray-500 hidden sm:inline">+ NHIL {nhil} · GETFund {getfund} · VAT {vat}</span>
              <span className="text-green-400 font-bold">TOTAL GHS {totalPrice}</span>
            </div>
          )}

          {currentStep === 3 && (
            <DetailsStep
              customerName={customerName} setCustomerName={setCustomerName}
              email={email} setEmail={setEmail}
              phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber}
              autoRenewal={autoRenewal} setAutoRenewal={setAutoRenewal}
            />
          )}

          {currentStep === 4 && (
            <ReviewStep
              selectedPlan={isCustom ? `Custom (${cpuCores}vCPU/${ramGb}GB)` : selectedPlan}
              selectedData={selectedData}
              selectedOS={selectedOS} selectedVariant={selectedVariant}
              quantity={quantity} billingCycle={billingCycle}
              pricePerMonth={pricePerMonth} totalPrice={totalPrice}
              subtotal={subtotal} nhil={nhil} getfund={getfund} vat={vat}
              isCustom={isCustom}
              customSpecs={{ cpuCores, ramGb, nvmeGb: customNvme, hddGb: customHdd }}
              dedicatedIPs={isStorageOnly ? 0 : dedicatedIPs}
              ipPriceTotal={isStorageOnly ? 0 : ipPriceTotal}
              IP_PRICE_PER_UNIT={IP_PRICE}
              extraStorage={isCustom ? { nvme: customNvme, hdd: customHdd } : (isStorageOnly ? { nvme: 0, hdd: 0 } : extraStorage)}
              storagePriceTotal={isCustom ? storagePriceTotal : (isStorageOnly ? 0 : storagePriceTotal)}
              STORAGE_PRICE_PER_UNIT={NVME_PRICE}
              autoRenewal={autoRenewal}
              startDate={startDate}
              formattedExpirationDate={formattedExpirationDate}
              agreeTerms={agreeTerms} setAgreeTerms={setAgreeTerms}
              isStorageOnly={isStorageOnly}
            />
          )}

          <div className="flex justify-between mt-6 space-x-4">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2.5 rounded-xl text-sm font-medium transition duration-200"
              >
                Previous
              </button>
            )}
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-medium transition duration-200"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading || !agreeTerms || !canSubmit}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white py-2.5 rounded-xl text-sm font-semibold transition duration-200"
              >
                {isLoading ? "Processing..." : `Subscribe Now - GHS ${totalPrice}`}
              </button>
            )}
          </div>

          <div className="text-center mt-3 text-xs text-gray-400">
            {currentStep === 1 && (isStorageOnly ? "Select your cloud storage plan" : isCustom ? "Pick Compute → Storage → System tabs above" : "Select your core VPS computing layer specs")}
            {currentStep === 2 && !isStorageOnly && !isCustom && "Configure optional dedicated IPs and expandable storage pools"}
            {currentStep === 2 && !isStorageOnly && isCustom && "Add dedicated IPs (storage already in your custom specs)"}
            {currentStep === 2 && isStorageOnly && "Review your storage plan details"}
            {currentStep === 3 && "Provide technical provisioning point-of-contact details"}
            {currentStep === 4 && "Confirm purchase details and verify terms agreement"}
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default SubscriptionModal;
